import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { CANCEL_REASONS_PATH } from '../../../../shared/contracts/job-request.js';
import { JobJourney, type JourneyStep } from '../../../../shared/components/JobJourney.js';
import { getJobJourney } from '../../api/jobJourneyApi.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * Pretty names for the stage codes the journey reports. A stage with no entry
 * here shows its own code, so a new one appears without waiting for a change.
 */
const STAGE_LABELS: Record<string, string> = {
  JOB: 'Job',
  APPROVAL: 'Approvals',
  BRANDING: 'Branding',
  VENDOR: 'Vendor',
  FIELD_FORCE: 'Field force',
  BO_FINAL: 'Final verification',
  QUANTITY: 'Quantity',
  BILLING: 'Billing',
  COMMERCIAL: 'Sales accounts',
};

/** T077 header — the only shape GET /v1/job-requests/:id returns. */
interface JobHeader {
  readonly jobRequestId: string;
  readonly jobNumber: string | null;
  readonly currentStatus: string;
  readonly customerId: string;
  readonly customerName: string;
  readonly territoryId: string;
  readonly workTypeId: string;
  readonly areaRequested: string | number;
  readonly areaUnit: string;
  /** The surface as measured on site; null on jobs raised before it was captured. */
  readonly height: string | number | null;
  readonly width: string | number | null;
  readonly latitude: string | number;
  readonly longitude: string | number;
  readonly locationAccuracy: string | number | null;
  readonly requestDate: string;
  readonly remarks: string | null;
  /** Set when submission started an M04 approval workflow. */
  readonly workflowInstanceId?: string | null;
  /**
   * The branding request this job raised, or null if it never reached the
   * branding queue. A submitted job with null here is stranded, which is a
   * fact the screen has to state rather than hide.
   */
  readonly brandingRequestNo?: string | null;
}

/**
 * One step of the approval trail, as M04 already reports it over
 * GET /v1/wf/instances/:id/trail. Read, never rebuilt here.
 */
interface WorkflowStep {
  readonly instanceStepId: string;
  readonly stepSequence: number;
  readonly stepName: string | null;
  readonly stepStatus: string;
  readonly assignedRoleCode: string | null;
  readonly assignedUserId: string | null;
  readonly actorName?: string | null;
  readonly actorLogin?: string | null;
}

/** T085 immutable status-transition log. */
interface StatusTransition {
  readonly historyId: string;
  readonly fromStatus: string | null;
  readonly toStatus: string;
  readonly transitionReason: string | null;
  readonly transitionedAt: string;
  readonly transitionedBy: string;
  /** t085 stores only a uuid; the name is joined in on read. */
  readonly transitionedByName?: string | null;
  readonly transitionedByCode?: string | null;
}

/**
 * Who made the transition, written the way a person is named rather than the
 * way a database stores them. The uuid remains the last resort: a row whose
 * actor no longer exists still has to say something truthful.
 */

function actorOf(h: StatusTransition): string {
  if (h.transitionedByName && h.transitionedByCode)
    return `${h.transitionedByName} (${h.transitionedByCode})`;
  return h.transitionedByName ?? h.transitionedByCode ?? h.transitionedBy;
}

/**
 * Where the request is waiting, in plain words.
 *
 * Three honest answers, never a guess:
 *   - a step is PENDING  → it is with that step's approver or role
 *   - no step is pending → the workflow has finished
 *   - no workflow at all → it is with nobody, which is a fault worth saying
 *     out loud. A job can reach SUBMITTED without an instance when its work
 *     type names a workflow the tenant does not have.
 */
function pendingWith(
  trail: readonly WorkflowStep[] | null,
  job: JobHeader | null,
  t: (k: string, v?: Record<string, string>) => string,
): string {
  if (trail === null) return t('common.loading');
  if (!job?.workflowInstanceId) return t('sales.detail.pendingNoWorkflow');
  const step = trail.find((s) => s.stepStatus === 'PENDING');
  if (!step) return t('sales.detail.pendingNone');
  const who =
    step.actorName ??
    step.assignedRoleCode ??
    step.assignedUserId ??
    t('sales.detail.pendingUnassigned');
  return t('sales.detail.pendingAt', {
    who,
    step: step.stepName ?? String(step.stepSequence),
  });
}

// M06-06 (UI-Audit C2) — cancellation reason sourced from the Rejection Reason
// Master (T063) instead of a hardcoded literal.
interface CancelReason {
  readonly reasonId: string;
  readonly reasonCode: string;
  readonly reasonDescription: string;
  readonly requiresRemarks: boolean;
  readonly sortOrder: number;
}

/**
 * The rows out of a response, whatever envelope it arrived in.
 *
 * This application answers in three shapes — a bare array, `{ data: [...] }`,
 * and `{ data: { <named list>: [...] } }` — and a reader that knows only the
 * first fails **silently**: it returns nothing, and nothing reads as "there is
 * nothing", not as "I could not find it".
 *
 * Measured during the UAT: the approval trail answers
 * `{ success, data: { stepTrail: [...] } }` with a genuine PENDING step on it,
 * and this screen reported *"No approval is pending"* while the job sat with
 * the Branding Officer. The data was right the whole way down; only the
 * reading was wrong.
 *
 * `listKey` names the list inside `data` when there is one. Nothing is
 * guessed: a caller that knows its envelope says so.
 */
const asList = <T,>(payload: unknown, listKey?: string): readonly T[] => {
  if (Array.isArray(payload)) return payload as T[];
  const data = (payload as { data?: unknown } | null)?.data;
  if (Array.isArray(data)) return data as T[];
  if (listKey) {
    const inner = (data as Record<string, unknown> | null)?.[listKey];
    if (Array.isArray(inner)) return inner as T[];
  }
  return [];
};

/** The header carries ids; these masters turn them into something readable. */
interface TerritoryRow { readonly territoryId: string; readonly territoryName: string }
interface WorkTypeRow { readonly workTypeId: string; readonly workTypeName: string }

export function SalesJobDetailPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day, at } = useBusinessDay();
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [job, setJob] = useState<JobHeader | null>(null);
  const [history, setHistory] = useState<readonly StatusTransition[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  /** null = not asked yet; [] = asked and the request is with nobody. */
  const [trail, setTrail] = useState<readonly WorkflowStep[] | null>(null);
  /** Why the trail could not be read, when it could not. Never silent. */
  const [trailError, setTrailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [reasons, setReasons] = useState<CancelReason[] | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [reasonId, setReasonId] = useState('');
  const [remarks, setRemarks] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [territories, setTerritories] = useState<readonly TerritoryRow[]>([]);
  const [workTypes, setWorkTypes] = useState<readonly WorkTypeRow[]>([]);
  /**
   * Raising the branding request for a job that never got one. The
   * measurement is only asked for when the job does not already carry it —
   * nothing is filled in on the officer's behalf.
   */
  const [showRaise, setShowRaise] = useState(false);
  const [raiseHeight, setRaiseHeight] = useState('');
  const [raiseWidth, setRaiseWidth] = useState('');
  const [raiseError, setRaiseError] = useState<string | null>(null);
  /**
   * The job's whole journey, not just its own status changes. Read separately
   * because it crosses every module, and failing softly: a job detail must
   * still open if the journey cannot be read.
   */
  const [journey, setJourney] = useState<readonly JourneyStep[]>([]);
  const [journeyError, setJourneyError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    getJobJourney(id, token)
      .then((r) => { if (alive) { setJourney(r.entries); setJourneyError(null); } })
      .catch((e) => { if (alive) setJourneyError((e as Error).message); });
    return () => { alive = false; };
  }, [id, token]);

  useEffect(() => {
    let alive = true;
    apiRequest<JobHeader>(`/v1/job-requests/${id}`, { token })
      .then((r) => { if (alive) setJob(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    // The log is a separate resource; it should not be able to blank out the
    // header if it fails — nor read as "no history" when it does.
    apiRequest<unknown>(`/v1/job-requests/${id}/history`, { token })
      .then((r) => { if (alive) { setHistory(asList<StatusTransition>(r)); setHistoryError(null); } })
      .catch((e) => { if (alive) setHistoryError((e as Error).message); });
    apiRequest<unknown>('/v1/territories?limit=200', { token })
      .then((r) => { if (alive) setTerritories(asList<TerritoryRow>(r)); }).catch(() => undefined);
    apiRequest<unknown>('/master/work-types?status=ACTIVE', { token })
      .then((r) => { if (alive) setWorkTypes(asList<WorkTypeRow>(r)); }).catch(() => undefined);
    return () => { alive = false; };
  }, [id, token]);

  // Where the request is waiting. M04 already answers this for any instance;
  // this reads that answer rather than working it out a second time here.
  useEffect(() => {
    let alive = true;
    const instanceId = job?.workflowInstanceId;
    if (!instanceId) { setTrail([]); setTrailError(null); return () => { alive = false; }; }
    apiRequest<unknown>(`/v1/wf/instances/${instanceId}/trail`, { token })
      .then((r) => { if (alive) { setTrail(asList<WorkflowStep>(r, 'stepTrail')); setTrailError(null); } })
      // A refused or failed read is not "nobody is holding it". Turning one
      // into the other is how a job with a pending approver came to read
      // "No approval is pending" — the quietest possible way to be wrong.
      .catch((e) => { if (alive) { setTrail([]); setTrailError((e as Error).message); } });
    return () => { alive = false; };
  }, [job?.workflowInstanceId, token]);

  async function submitJob() {
    if (!job) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      // The workflow definition is resolved server-side from the work type.
      await apiRequest<JobHeader>(`/v1/job-requests/${job.jobRequestId}/submit`, { method: 'POST', token, body: {} });
      setInfo(t('sales.detail.submittedInfo'));
      // Read the job back rather than trusting the submit response.
      //
      // Submitting does two things: it starts the approval, and it raises the
      // branding request line. The submit endpoint answers with the T077 header
      // alone — `brandingRequestNo` is added only by the detail read, which
      // says so in its own words: "this is the read the screen uses".
      //
      // Showing the submit response therefore left the screen contradicting
      // itself: "Pending at: BRANDING_OFFICER" from the fresh workflow trail,
      // and "this job has not reached the branding queue" from a header that
      // never carried the number. Both were drawn from the same click. The
      // same re-read already follows `raiseBrandingRequest` for exactly this
      // reason.
      const refreshed = await apiRequest<JobHeader>(`/v1/job-requests/${job.jobRequestId}`, { token });
      setJob(refreshed);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }

  /**
   * Raise the branding request line for a job that is submitted but never got
   * one. The server refuses a second one outright, so this cannot duplicate
   * the work; it only has to carry the measurement when the job lacks it.
   */
  async function raiseBrandingRequest() {
    if (!job) return;
    const needsMeasurement = job.height == null || job.width == null;
    const h = Number(raiseHeight);
    const w = Number(raiseWidth);
    if (needsMeasurement && !(h > 0 && w > 0)) {
      setRaiseError(t('sales.detail.measurementRequired'));
      return;
    }
    setBusy(true); setRaiseError(null); setError(null); setInfo(null);
    try {
      const res = await apiRequest<{ requestNo: string }>(
        `/v1/job-requests/${job.jobRequestId}/branding-request`,
        { method: 'POST', token, body: needsMeasurement ? { height: h, width: w } : {} },
      );
      setInfo(t('sales.detail.brandingRaised', { request: res.requestNo }));
      setShowRaise(false);
      const refreshed = await apiRequest<JobHeader>(`/v1/job-requests/${job.jobRequestId}`, { token });
      setJob(refreshed);
    } catch (e) { setRaiseError((e as Error).message); } finally { setBusy(false); }
  }

  async function openCancel() {
    setShowCancel(true);
    setCancelError(null);
    if (reasons === null) {
      try {
        const r = await apiRequest<unknown>(CANCEL_REASONS_PATH, { token });
        setReasons([...asList<CancelReason>(r)]);
      } catch (e) { setReasons([]); setCancelError((e as Error).message); }
    }
  }

  async function confirmCancel() {
    if (!job) return;
    const chosen = (reasons ?? []).find((r) => r.reasonId === reasonId);
    if (!chosen) { setCancelError(t('sales.detail.selectReason')); return; }
    if (chosen.requiresRemarks && !remarks.trim()) { setCancelError(t('sales.detail.remarksRequired')); return; }
    setBusy(true); setError(null); setInfo(null); setCancelError(null);
    try {
      await apiRequest(`/v1/job-requests/${job.jobRequestId}/cancel`, {
        method: 'POST', token,
        body: { reasonId: chosen.reasonId, reasonText: chosen.reasonDescription, additionalNotes: remarks.trim() || null },
      });
      setInfo(t('sales.detail.cancelledInfo'));
      setJob({ ...job, currentStatus: 'CANCELLED' });
      setShowCancel(false);
    } catch (e) { setCancelError((e as Error).message); } finally { setBusy(false); }
  }

  if (error) return <main><p role="alert">{error}</p></main>;
  if (!job) return <main><p>{t('sales.detail.loading')}</p></main>;

  const open = job.currentStatus === 'DRAFT' || job.currentStatus === 'SUBMITTED';
  const territoryName =
    territories.find((x) => x.territoryId === job.territoryId)?.territoryName ?? job.territoryId;
  const workTypeName =
    workTypes.find((x) => x.workTypeId === job.workTypeId)?.workTypeName ?? job.workTypeId;
  const hasGps = Number.isFinite(Number(job.latitude)) && Number.isFinite(Number(job.longitude));

  return (
    <main aria-label="sales-job-detail">
      <h1>{job.jobNumber ?? t('sales.detail.draftNoNumber')}</h1>
      <p aria-label="job-status">{t('sales.detail.status')}: {job.currentStatus}</p>
      <dl>
        <dt>{t('sales.detail.customer')}</dt><dd aria-label="job-customer">{job.customerName}</dd>
        <dt>{t('sales.new.territory')}</dt><dd aria-label="job-territory">{territoryName}</dd>
        <dt>{t('sales.detail.workType')}</dt><dd aria-label="work-type">{workTypeName}</dd>
        <dt>{t('sales.detail.area')}</dt><dd aria-label="job-area">{Number(job.areaRequested)} {unitLabel(job.areaUnit)}</dd>
        {job.height != null && job.width != null ? (
          <>
            <dt>{t('sales.detail.measured')}</dt>
            <dd aria-label="job-measurement">
              {Number(job.height)} × {Number(job.width)} {unitLabel(job.areaUnit)}
            </dd>
          </>
        ) : null}
        <dt>{t('sales.detail.location')}</dt><dd aria-label="job-gps">{hasGps ? `${Number(job.latitude)}, ${Number(job.longitude)}` : '—'}</dd>
        <dt>{t('sales.detail.requestDate')}</dt><dd aria-label="job-request-date">{day(job.requestDate)}</dd>
      </dl>

      {info ? <p role="status">{info}</p> : null}

      {/*
        Where the request is waiting. A job that has been submitted but started
        no approval workflow is with nobody at all — that has to be visible on
        the screen, not left for someone to discover when nothing ever happens.
      */}
      <p aria-label="pending-with">
        {trailError
          ? t('sales.detail.pendingUnreadable', { reason: trailError })
          : pendingWith(trail, job, t)}
      </p>

      {/*
        Whether the job ever reached the branding queue. A job raises its
        branding request when it is forwarded; if that attempt failed — the
        sub-type was withdrawn at the time, the measurement was missing — the
        job stayed SUBMITTED and invisible to the branding team with no way
        back. Saying so here, and offering to raise it, is that way back.
      */}
      {job.currentStatus !== 'DRAFT' && job.currentStatus !== 'CANCELLED' ? (
        <section aria-label="branding-request-state">
          {job.brandingRequestNo ? (
            <p aria-label="branding-request-raised">
              {t('sales.detail.brandingRaisedAs', { request: job.brandingRequestNo })}
            </p>
          ) : (
            <>
              <p role="alert" aria-label="branding-request-missing">
                {t('sales.detail.brandingMissing')}
              </p>
              {showRaise ? (
                <div aria-label="raise-branding-panel">
                  {raiseError ? <p role="alert">{raiseError}</p> : null}
                  {job.height == null || job.width == null ? (
                    <>
                      <p>{t('sales.detail.measurementAsk')}</p>
                      <label htmlFor="raise-height">{t('sales.new.height')}</label>
                      <input
                        id="raise-height"
                        aria-label="raise-height"
                        type="number"
                        min="0"
                        step="0.01"
                        value={raiseHeight}
                        onChange={(e) => setRaiseHeight(e.target.value)}
                      />
                      <label htmlFor="raise-width">{t('sales.new.width')}</label>
                      <input
                        id="raise-width"
                        aria-label="raise-width"
                        type="number"
                        min="0"
                        step="0.01"
                        value={raiseWidth}
                        onChange={(e) => setRaiseWidth(e.target.value)}
                      />
                      <p aria-label="raise-area">
                        {t('sales.new.areaIs', {
                          area: String(Number(raiseHeight) * Number(raiseWidth) || 0),
                          unit: unitLabel(job.areaUnit),
                        })}
                      </p>
                    </>
                  ) : null}
                  <button
                    onClick={raiseBrandingRequest}
                    aria-label="confirm-raise-branding"
                    disabled={busy}
                    type="button"
                  >
                    {t('sales.detail.brandingRaiseConfirm')}
                  </button>
                  <button
                    onClick={() => { setShowRaise(false); setRaiseError(null); }}
                    aria-label="cancel-raise-branding"
                    disabled={busy}
                    type="button"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setShowRaise(true); setRaiseError(null); }}
                  aria-label="raise-branding"
                  disabled={busy}
                  type="button"
                >
                  {t('sales.detail.brandingRaise')}
                </button>
              )}
            </>
          )}
        </section>
      ) : null}

      {/*
        The status history is only the job's own transitions. The journey is
        the whole of it — the vendor, the field force, the evidence, the
        approval levels, the billing — drawn as a line so a glance says how far
        this job has got. Every stakeholder opens the same screen and sees the
        same picture; what differs is what each may do on it.
      */}
      <h2>{t('sales.detail.timeline')}</h2>
      {journeyError ? (
        <p role="alert" aria-label="journey-error">{journeyError}</p>
      ) : null}
      <JobJourney
        steps={journey}
        stageLabels={STAGE_LABELS}
        emptyMessage={t('sales.detail.noHistory')}
        ariaLabel="job-journey"
      />

      {/* The job's own status transitions, kept because they are the one
          record that exists before anything else has happened to it. */}
      {historyError
        ? <p role="alert" aria-label="history-error">{historyError}</p>
        : history.length === 0
        ? <p aria-label="no-history">{t('sales.detail.noHistory')}</p>
        : <ol aria-label="status-timeline">
            {history.map((h, i) => (
              <li key={h.historyId} aria-label={`hist-${i}`}>
                {h.fromStatus ? `${h.fromStatus} → ` : ''}{h.toStatus} · {at(h.transitionedAt)} · {actorOf(h)}
              </li>
            ))}
          </ol>}

      <div>
        {job.currentStatus === 'DRAFT' ? (
          <button onClick={submitJob} aria-label="submit-job" disabled={busy} type="button">{t('sales.detail.submitReview')}</button>
        ) : null}
        {open ? <Link to={`/sales/jobs/${job.jobRequestId}/edit`} aria-label="edit-job">{t('sales.detail.edit')}</Link> : null}
        {open ? (
          <button onClick={openCancel} aria-label="cancel-job" disabled={busy} type="button">{t('sales.detail.cancel')}</button>
        ) : null}
      </div>

      {showCancel ? (
        <section aria-label="cancel-panel">
          <h2>{t('sales.detail.cancelTitle')}</h2>
          {cancelError ? <p role="alert">{cancelError}</p> : null}
          <div>
            <label htmlFor="cancel-reason-select">{t('sales.detail.reason')}</label>
            <select
              id="cancel-reason-select"
              aria-label="cancel-reason"
              value={reasonId}
              onChange={(e) => { setReasonId(e.target.value); setCancelError(null); }}
            >
              <option value="">{t('sales.detail.selectPlaceholder')}</option>
              {(reasons ?? []).map((r) => (
                <option key={r.reasonId} value={r.reasonId}>{r.reasonDescription}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="cancel-remarks-input">{t('sales.detail.remarks')}</label>
            <textarea
              id="cancel-remarks-input"
              aria-label="cancel-remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>
          <button onClick={confirmCancel} aria-label="confirm-cancel" disabled={busy} type="button">{t('sales.detail.confirmCancel')}</button>
          <button onClick={() => setShowCancel(false)} aria-label="dismiss-cancel" disabled={busy} type="button">{t('sales.detail.keepJob')}</button>
        </section>
      ) : null}
    </main>
  );
}
